import { useState, useRef, useCallback } from 'react';
import useDismiss from '../custome_hooks/useDismiss';
import * as XLSX from 'xlsx';
import { Button } from '../core/Button';
import { ChevronLeft, ChevronRight, CloudDownload, Download, Send } from 'lucide-react';
import { usePayrollOfficeOverview, useHasSickReports, useIsPublished } from '../../graphql/hooks/useAdminDashboard';
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { DEFAULT_ENDPOINT_URL_EXPORT } from '../../graphql/client';
import { useAuth } from '../../graphql/hooks/useAuth';
import { useFetchDataFromDatev } from '../../graphql/hooks/useAdminDashboard';
import { useHasExistingDatevData } from '../../graphql/hooks/useAdminDashboard';
import { usePublishDatevData } from '../../graphql/hooks/useAdminDashboard';
import toast from 'react-hot-toast';
import { Loader2 } from 'lucide-react';
import { useSendDataToDatev, useDatevSendStatus } from '../../graphql/hooks/useAdminDashboard';
import { useDatevDataSendable } from '../../graphql/hooks/useAdminDashboard';
import { useGetValidToken } from '../../graphql/hooks/useAdminDashboard';

export function PayrollView() {
  const { token } = useAuth();
  const [showMonthPicker, setShowMonthPicker] = useState(false);
  const monthPickerRef = useRef<HTMLDivElement>(null);
  const closeMonthPicker = useCallback(() => setShowMonthPicker(false), []);
  useDismiss(monthPickerRef, showMonthPicker, closeMonthPicker);
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const { mutate: fetchFromDatev, isPending: isFetchingDatev } = useFetchDataFromDatev();
  const { mutate: sendToDatev, isPending: isSendingDatev } = useSendDataToDatev();
  const { data: hasValidToken } = useGetValidToken();

  const getCurrentMonthString = () => {
    const now = new Date();
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, '0');
    return `${year}-${month}`;
  };

  const [selectedMonth, setSelectedMonth] = useState(getCurrentMonthString());

  const [year, month] = selectedMonth
    .split('-')
    .map(Number);

  const { data: payrollInfo } = usePayrollOfficeOverview({ month, year })
  const payrollData = payrollInfo?.getPayrollOfficeOverview ?? []
  const { data: hasSickReports } = useHasSickReports(month, year);
  const { data: hasDatevData } = useHasExistingDatevData(month, year);
  const { data: isPublished, isLoading } = useIsPublished(month, year);
  const { data: datevDataSendable } = useDatevDataSendable(month, year);
  const {
    data: sendStatus,
    isTimedOut: isSendTimedOut,
    resumePolling: resumeSendPolling,
  } = useDatevSendStatus(month, year);
  const isSendRunning = sendStatus?.status === 'running' && !isSendTimedOut;
  const failedPersonnelNumbers = sendStatus?.failed_personnel_numbers ?? [];

  const monthNames = [
    'Januar', 'Februar', 'März', 'April',
    'Mai', 'Juni', 'Juli', 'August',
    'September', 'Oktober', 'November', 'Dezember'
  ];

  const formatMonthLabel = (value: string) => {
    const [year, month] = value.split('-');
    return `${monthNames[Number(month) - 1]} ${year}`;
  };

 const { mutate: publishDatevData, isPending: isPublishingDatev } = usePublishDatevData();

  const toastSuccess = (message: string) =>
    toast.success(message, {
      duration: 3000,
      style: {
        background: '#f0fdf4', border: '1px solid #bbf7d0',
        color: '#15803d', padding: '12px 14px',
        borderRadius: '8px', fontSize: '14px',
      },
      iconTheme: { primary: '#16a34a', secondary: '#f0fdf4' },
  });

  const toastError = (message: string) =>
    toast.error(message, {
      duration: 4000,
      style: {
        background: '#fef2f2', border: '1px solid #fecaca',
        color: '#dc2626', padding: '12px 14px',
        borderRadius: '8px', fontSize: '14px',
      },
      iconTheme: { primary: '#dc2626', secondary: '#fef2f2' },
  });


  const formatMonthForState = (date: Date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    return `${year}-${month}`;
  };

  const renderMonthPicker = () => {
    const months = [
      'Jan', 'Feb', 'Mär', 'Apr',
      'Mai', 'Jun', 'Jul', 'Aug',
      'Sep', 'Okt', 'Nov', 'Dez'
    ];

    return (
      <>
        {/* Header */}
        <div className="flex items-center justify-between mb-4">
          <button
            onClick={() =>
              setCurrentMonth(
                new Date(currentMonth.getFullYear() - 1, 0, 1)
              )
            }
          >
            <ChevronLeft className="size-5 text-[#6b6b6b]" />
          </button>

          <div className="text-sm font-medium">
            {currentMonth.getFullYear()}
          </div>

          <button
            onClick={() =>
              setCurrentMonth(
                new Date(currentMonth.getFullYear() + 1, 0, 1)
              )
            }
          >
            <ChevronRight className="size-5 text-[#6b6b6b]" />
          </button>
        </div>

        {/* Months grid */}
        <div className="grid grid-cols-3 gap-2">
          {months.map((month, index) => {
            const isSelected =
              selectedMonth ===
              formatMonthForState(
                new Date(currentMonth.getFullYear(), index, 1)
              );

            return (
              <button
                key={month}
                onClick={() => {
                  const newDate = new Date(
                    currentMonth.getFullYear(),
                    index,
                    1
                  );

                  setSelectedMonth(formatMonthForState(newDate));
                  setCurrentMonth(newDate);
                  setShowMonthPicker(false);
                }}
                className={`h-10 rounded-md text-sm transition-colors
                  ${
                    isSelected
                      ? 'bg-[#0084c7] text-white'
                      : 'hover:bg-[#f5f5f5]'
                  }
                `}
              >
                {month}
              </button>
            );
          })}
        </div>
      </>
    );
  };

  const downloadXLSX = () => {
    if (!payrollData?.length) return;
 
    const headers = [
      "Name",
      "Beruf",
      "Status",
      "Pers-Nr",
      "Eintritt",
      "Sollstunden",
      "Überstunden Auszahlung",
      "Nachtarbeit",
      "Notdienst €",
      "Spesen €",
      "Sonntagsarbeit",
      "Bemerkung"
    ];
 
    const rows = payrollData.map(entry => [
      entry.name,
      entry.job_title,
      entry.status,
      entry.internal_number,
      entry.entry_date,
      entry.month_hours,
      entry.payed_overtime_hours,
      entry.night_work,
      entry.emergencs_service,
      entry.expenses,
      entry.sunday_work,
      entry.comment ?? ""
    ]);
 
    const totalsRow = [
      "Gesamt", "", "", "", "", "",
      rows.reduce((sum, r) => sum + (Number(r[6]) || 0), 0),
      rows.reduce((sum, r) => sum + (Number(r[7]) || 0), 0),
      rows.reduce((sum, r) => sum + (Number(r[8]) || 0), 0),
      rows.reduce((sum, r) => sum + (Number(r[9]) || 0), 0),
      rows.reduce((sum, r) => sum + (Number(r[10]) || 0), 0),
      "",
    ];
 
    const worksheet = XLSX.utils.aoa_to_sheet([headers, ...rows, totalsRow]);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Lohnauswertung');
 
    const totalsRowIndex = rows.length + 1; 
    [6, 7, 8, 9, 10].forEach(colIndex => {
      const cellRef = XLSX.utils.encode_cell({ r: totalsRowIndex, c: colIndex });
      if (worksheet[cellRef]) {
        worksheet[cellRef].s = { font: { bold: true } };
      }
    });
 
    const allRows = [...rows, totalsRow];
    const colWidths = headers.map((h, i) => ({
      wch: Math.max(h.length, ...allRows.map(r => String(r[i] ?? '').length)) + 2,
    }));
    worksheet['!cols'] = colWidths;
 
    XLSX.writeFile(workbook, `Lohnauswertung_${selectedMonth}.xlsx`);
  };

  const downloadPDF = () => {
    if (!payrollData?.length) return;
 
    const doc = new jsPDF({
      orientation: "landscape", 
    });
 
    const tableColumn = [
      "Name",
      "Beruf",
      "Status",
      "Pers-Nr",
      "Eintritt",
      "Sollstunden",
      "Überstunden Auszahlung",
      "Nachtarbeit",
      "Notdienst €",
      "Spesen €",
      "Sonntagsarbeit",
      "Bemerkung"
    ];
 
    const tableRows = payrollData.map(entry => [
      entry.name,
      entry.job_title,
      entry.status,
      entry.internal_number,
      entry.entry_date,
      entry.month_hours,
      entry.payed_overtime_hours,
      entry.night_work,
      entry.emergencs_service,
      entry.expenses,
      entry.sunday_work,
      entry.comment ?? ""
    ]);
 
    const totalsPdfRow = [
      "Gesamt", "", "", "", "", "",
      tableRows.reduce((sum, r) => sum + (Number(r[6]) || 0), 0),
      tableRows.reduce((sum, r) => sum + (Number(r[7]) || 0), 0),
      tableRows.reduce((sum, r) => sum + (Number(r[8]) || 0), 0),
      tableRows.reduce((sum, r) => sum + (Number(r[9]) || 0), 0),
      tableRows.reduce((sum, r) => sum + (Number(r[10]) || 0), 0),
      "",
    ];
 
    doc.text(`Lohnauswertung ${formatMonthLabel(selectedMonth)}`, 14, 15);
 
    autoTable(doc, {
      head: [tableColumn],
      body: [...tableRows, totalsPdfRow],
      startY: 20,
      styles: { fontSize: 8 },
      headStyles: { fillColor: [0, 132, 199] },
      didParseCell: (data) => {
        if (data.section === 'body' && data.row.index === tableRows.length) {
          data.cell.styles.fontStyle = 'bold';
        }
      },
    });
 
    doc.save(`Lohnauswertung_${selectedMonth}.pdf`);
  };

  const handleDownloadSickLeave = async () => {
    try {

      const response = await fetch(
        `${DEFAULT_ENDPOINT_URL_EXPORT}/sickreports/download?month=${month}&year=${year}`,
        {
          method: "GET",
          headers: {
            Authorization:  token,
            Accept: "application/zip",
          },
        }
      );

      if (!response.ok) {
        const text = await response.text();
        console.error("Download failed:", text);
        return;
      }

      const blob = await response.blob();

      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `sickreports_${year}_${month}.zip`;
      a.click();
      window.URL.revokeObjectURL(url);

    } catch (err) {
      console.error(err);
    }
  };


  const [payrollCurrentPage, setPayrollCurrentPage] = useState(1);
  const [payrollItemsPerPage, setPayrollItemsPerPage] = useState(10);

  const [payrollSearchTerm, setPayrollSearchTerm] = useState('');

  return (
    <div className="space-y-6">
    {/* Controls Row */}
    <div className="flex flex-col gap-2">
      {/* Row 1 - everything as before */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
        {/* Left Side - Month Selector and Items Per Page */}
        <div className="flex items-center gap-4">
          <div className="py-3">
            <div ref={monthPickerRef} className="relative">
              <button
                onClick={() => setShowMonthPicker(prev => !prev)}
                className="px-4 py-2 border border-[#e9e9e9] rounded-lg text-[14px] text-[#1c1c1c] bg-white hover:border-[#0084c7]"
              >
                {formatMonthLabel(selectedMonth)}
              </button>

              {showMonthPicker && (
                <div className="absolute mt-2 w-[260px] bg-white border border-[#e9e9e9] rounded-[12px] shadow-lg p-4 z-30">
                  {renderMonthPicker()}
                </div>
              )}
            </div>
          </div>
          <div className="flex items-center gap-2 text-[#6b6b6b] text-[14px]">
            <span>Zeige</span>
            <select
              value={payrollItemsPerPage}
              onChange={(e) => {
                setPayrollItemsPerPage(Number(e.target.value));
                setPayrollCurrentPage(1);
              }}
              className="form-select px-2 py-1 border border-[#e0e0e0] rounded text-[#1c1c1c]"
            >
              <option value={10}>10</option>
              <option value={25}>25</option>
              <option value={50}>50</option>
            </select>
            <span>Einträge</span>
          </div>
        </div>

        {/* Right Side - Export Buttons */}
          <div className="flex items-center gap-2">
            <Button
              variant="primary"
              onClick={handleDownloadSickLeave}
              disabled={!hasSickReports}
              className="flex items-center gap-2"
            >
              <Download className="size-5" />
              Krankmeldungen herunterladen
            </Button>
            <Button
              variant="secondary"
              onClick={downloadXLSX}
              className="flex items-center gap-2 bg-green-600 text-white hover:bg-green-700 border-green-600"
            >
              Excel
            </Button>
            <Button
              variant="secondary"
              onClick={downloadPDF}
              className="flex items-center gap-2 bg-red-600 text-white hover:bg-red-700 border-red-600"
            >
              PDF
            </Button>
            </div>
          </div>
          
          {/* Row 2 - Send to DATEV */}
          {hasValidToken && (
            <div className="flex justify-end gap-2">
              <Button
                variant="primary"
                disabled={!!hasDatevData && hasDatevData.length > 0 || isFetchingDatev}
                onClick={() =>
                  fetchFromDatev(
                    { month, year },
                    {
                      onSuccess: (data) => {
                        if (data === false) {
                          toastError('Keine Daten gefunden oder Abruf fehlgeschlagen');
                          return;
                        }
                        toastSuccess('Dokumente erfolgreich abgerufen');
                      },
                      onError: (error: any) => {
                        const datevAuthError = error?.response?.errors?.find(
                          (e: any) => e?.extensions?.code === 'DATEV_AUTH_REQUIRED'
                        );
                        if (datevAuthError?.extensions?.loginUrl) {
                          const a = document.createElement('a');
                          a.href = datevAuthError.extensions.loginUrl;
                          a.target = '_blank';
                          a.rel = 'noopener noreferrer';
                          document.body.appendChild(a);
                          a.click();
                          document.body.removeChild(a);
                        } else {
                          toastError('Fehler beim Abrufen der Dokumente');
                        }
                      },
                    }
                  )
                }
                className="flex items-center gap-2"
              >
                {isFetchingDatev ? (
                  <div className="size-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                ) : (
                  <CloudDownload className="size-5" />
                )}
                {isFetchingDatev ? 'Wird abgerufen...' : 'Dokumente abrufen'}
              </Button>
              <Button
                variant="primary"
                onClick={() => publishDatevData(
                  { month, year },
                  {
                    onSuccess: () => toastSuccess('Dokumente erfolgreich verteilt'),
                    onError: () => toastError('Fehler beim Verteilen der Dokumente'),
                  }
                )}
                className="flex items-center gap-2"
                disabled={!!isPublished || !hasDatevData || hasDatevData.length === 0}
              >
                {isPublishingDatev ? (
                  <div className="size-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                ) : (
                  <CloudDownload className="size-5" />
                )}
                {isPublishingDatev ? 'Wird verteilt...' : 'Dokumente verteilen'}
              </Button>
              <Button
                variant="primary"
                onClick={() =>
                  sendToDatev(
                    { month, year },
                    {
                      onSuccess: (started) => {
                        if (started === false) {
                          toastError('Keine Abrechnungsdaten für diesen Monat vorhanden');
                          return;
                        }
                        resumeSendPolling();
                        toastSuccess('Übertragung an DATEV gestartet');
                      },
                      onError: (error: any) => {
                        const datevAuthError = error?.response?.errors?.find(
                          (e: any) => e?.extensions?.code === 'DATEV_AUTH_REQUIRED'
                        );
                        if (datevAuthError?.extensions?.loginUrl) {
                          const a = document.createElement('a');
                          a.href = datevAuthError.extensions.loginUrl;
                          a.target = '_blank';
                          a.rel = 'noopener noreferrer';
                          document.body.appendChild(a);
                          a.click();
                          document.body.removeChild(a);
                        } else {
                          toastError('Fehler beim Senden der Daten an DATEV');
                        }
                      },
                    }
                  )
                }
                className="flex items-center gap-2"
                disabled={(datevDataSendable && datevDataSendable.length > 0) || isSendingDatev || isSendRunning}
              >
                {isSendingDatev || isSendRunning ? (
                  <div className="size-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                ) : (
                  <Send className="size-5" />
                )}
                {isSendingDatev || isSendRunning ? 'Wird gesendet...' : 'Daten senden'}
              </Button>
            </div>
          )}

          {isSendRunning && (
            <div className="flex items-center gap-2 px-4 py-3 rounded-lg border border-[#e0e0e0] bg-[#f5f5f5] text-[14px] text-[#6b6b6b]">
              <Loader2 className="size-4 animate-spin shrink-0" />
              <span>
                Daten werden an DATEV gesendet... Das kann einige Minuten dauern. Sie können die Seite verlassen, der Versand läuft weiter.
              </span>
            </div>
          )}

          {isSendTimedOut && (
            <div className="px-4 py-3 rounded-lg border border-amber-200 bg-amber-50 text-[14px] text-amber-700">
              Der Versand dauert ungewöhnlich lange. Bitte prüfen Sie das DATEV-Protokoll.
            </div>
          )}

          {sendStatus?.status === 'completed' && failedPersonnelNumbers.length === 0 && (
            <div className="px-4 py-3 rounded-lg border border-green-200 bg-green-50 text-[14px] text-green-700">
              Alle Mitarbeiter erfolgreich an DATEV übertragen ({sendStatus.sent ?? 0} von {sendStatus.total ?? 0}).
            </div>
          )}

          {sendStatus?.status === 'completed' && failedPersonnelNumbers.length > 0 && (
            <div className="px-4 py-3 rounded-lg border border-amber-200 bg-amber-50 text-[14px] text-amber-700">
              {sendStatus.sent ?? 0} von {sendStatus.total ?? 0} Mitarbeitern übertragen. Fehlgeschlagen
              (Personalnummern): {failedPersonnelNumbers.join(', ')}
            </div>
          )}

          {sendStatus?.status === 'failed' && (
            <div className="px-4 py-3 rounded-lg border border-red-200 bg-red-50 text-[14px] text-red-600">
              Versand an DATEV fehlgeschlagen: {sendStatus.error || 'Unbekannter Fehler'}
            </div>
          )}
       
      </div>

      {/* DATEV Documents Table */}
      
      {hasDatevData && hasDatevData.length > 0 && (
        <div className="space-y-4">
          <h2 className="text-[#1c1c1c] text-[18px] font-medium pt-4">DATEV Dokumente</h2>
          <div className="overflow-x-auto">
            <div className="grid grid-cols-[1fr_150px] gap-4 px-4 py-3 mb-2 bg-[#f5f5f5] rounded-lg text-[12px] font-medium text-[#6b6b6b]">
              <div>Datei</div>
              <div>Aktion</div>
            </div>

            {hasDatevData.map((doc) => (
              <div
                key={doc.id}
                className="grid grid-cols-[1fr_150px] gap-4 px-4 py-3 text-[14px] items-center hover:bg-[#f5f5f5] transition-colors border-b border-[#e9e9e9]"
              >
                <div className="text-[#6b6b6b] truncate max-w-[300px]">
                  {doc.path.split('/').pop()}
                </div>
                <div>
                  <button
                    onClick={async () => {
                      try {
                        const response = await fetch(
                          `${DEFAULT_ENDPOINT_URL_EXPORT}/datev/download/${doc.id}`,
                          {
                            method: 'GET',
                            headers: {
                              Authorization: token,
                            },
                          }
                        );

                        if (!response.ok) {
                          console.error('Download failed:', await response.text());
                          return;
                        }

                          const blob = await response.blob();
                          const url = window.URL.createObjectURL(blob);

                          if (doc.file_type === 'pdf') {
                            const pdfBlob = new Blob([blob], { type: 'application/pdf' });
                            const pdfUrl = window.URL.createObjectURL(pdfBlob);
                            window.open(pdfUrl, '_blank');
                          } else {
                            const a = document.createElement('a');
                            a.href = url;
                            a.download = doc.path.split('/').pop() ?? `datev_${doc.id}.zip`;
                            a.click();
                            window.URL.revokeObjectURL(url);
                          }
                      } catch (err) {
                        console.error(err);
                      }
                    }}
                    className="flex items-center gap-1.5 px-3 py-1.5 text-[13px] text-white bg-[#0084c7] hover:bg-[#006fa8] rounded-lg transition-colors"
                  >
                    <Download className="size-4" />
                    {doc.file_type === 'pdf' ? 'Vorschau' : 'Herunterladen'}
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
     

      {/* Data Display */}
      <h2 className="text-[#1c1c1c] text-[18px] font-medium pt-4">Übersicht</h2>

      {payrollData?.length === 0 ? (
        <div className="py-12 text-center">
          <p className="text-[#6b6b6b] text-[16px]">
            Für den ausgewählten Monat sind noch keine Daten vorhanden
          </p>
        </div>
      ) : (
        <>
          <div className="space-y-4 overflow-x-auto">
            {/* Table Header */}
            <div className="grid grid-cols-[150px_200px_120px_80px_100px_100px_120px_100px_100px_100px_120px_120px] gap-4 px-4 py-3 bg-[#f5f5f5] rounded-lg text-[12px] font-medium text-[#6b6b6b] min-w-max">
              <div>Name</div>
              <div>Beruf</div>
              <div>Status</div>
              <div>Pers-Nr.</div>
              <div>Eintritt</div>
              <div className="text-right">Soll Stunden Gesamt in h</div>
              <div className="text-right">Überstunden zur Auszahlung in h</div>
              <div className="text-right">Nachtarbeit in h</div>
              <div className="text-right">Notdienst in €</div>
              <div className="text-right">Spesen in €</div>
              <div className="text-right">Sonntagsarbeit</div>
              <div>Bemerkung</div>
            </div>

            {/* Table Rows */}
            {(() => {
              const filteredData = payrollData.filter(entry => {
                const searchLower = payrollSearchTerm.toLowerCase();
                return (
                  entry.name.toLowerCase().includes(searchLower) ||
                  entry.job_title.toLowerCase().includes(searchLower) ||
                  entry.status.toLowerCase().includes(searchLower) ||
                  entry.internal_number.toLowerCase().includes(searchLower) ||
                  entry.entry_date.toLowerCase().includes(searchLower)
                );
              });

              const indexOfLastItem = payrollCurrentPage * payrollItemsPerPage;
              const indexOfFirstItem = indexOfLastItem - payrollItemsPerPage;
              const currentItems = filteredData.slice(indexOfFirstItem, indexOfLastItem);
              return currentItems.map((entry) => (
                <div
                  key={`${entry.name} ${entry.month_hours}`}
                  className="grid grid-cols-[150px_200px_120px_80px_100px_100px_120px_100px_100px_100px_120px_120px] gap-4 px-4 py-3 text-[14px] items-center hover:bg-[#f5f5f5] transition-colors min-w-max"
                >
                  <div className="text-[#1c1c1c]">{entry.name}</div>
                  <div className="text-[#6b6b6b]">{entry.job_title}</div>
                  <div className="text-[#6b6b6b]">{entry.status}</div>
                  <div className="text-[#6b6b6b]">{entry.internal_number}</div>
                  <div className="text-[#6b6b6b]">{entry.entry_date}</div>
                  <div className="text-[#6b6b6b] text-right">{entry.month_hours}</div>
                  <div className="text-[#6b6b6b] text-right">{entry.payed_overtime_hours}</div>
                  <div className="text-[#6b6b6b] text-right">{entry.night_work}</div>
                  <div className="text-[#6b6b6b] text-right">{entry.emergencs_service}</div>
                  <div className="text-[#6b6b6b] text-right">{entry.expenses}</div>
                  <div className="text-[#6b6b6b] text-right">{entry.sunday_work}</div>
                  <div className="text-[#6b6b6b]">{entry.comment}</div>
                </div>
              ));
            })()}
          </div>

          {/* Pagination */}
          <div className="flex items-center justify-between pt-4 border-t border-[#e0e0e0]">
            <div className="text-[#6b6b6b] text-[14px]">
              Zeige {Math.min((payrollCurrentPage - 1) * payrollItemsPerPage + 1, payrollData.length)} bis {Math.min(payrollCurrentPage * payrollItemsPerPage, payrollData.length)} von {payrollData.length} Einträgen
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setPayrollCurrentPage(prev => Math.max(1, prev - 1))}
                disabled={payrollCurrentPage === 1}
                className="px-3 py-1 text-[14px] text-[#6b6b6b] hover:text-[#0084c7] disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                Vorherige
              </button>

              {(() => {
                const totalPages = Math.ceil(payrollData.length / payrollItemsPerPage);
                const pageNumbers = [];
                const maxVisiblePages = 5;
                let startPage = Math.max(1, payrollCurrentPage - Math.floor(maxVisiblePages / 2));
                let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);
                if (endPage - startPage < maxVisiblePages - 1) {
                  startPage = Math.max(1, endPage - maxVisiblePages + 1);
                }
                for (let i = startPage; i <= endPage; i++) pageNumbers.push(i);
                return pageNumbers.map(number => (
                  <button
                    key={number}
                    onClick={() => setPayrollCurrentPage(number)}
                    className={`px-3 py-1 text-[14px] rounded transition-colors ${
                      payrollCurrentPage === number
                        ? 'bg-[#0084c7] text-white'
                        : 'text-[#6b6b6b] hover:bg-[#f5f5f5]'
                    }`}
                  >
                    {number}
                  </button>
                ));
              })()}

              <button
                onClick={() => setPayrollCurrentPage(prev => Math.min(Math.ceil(payrollData.length / payrollItemsPerPage), prev + 1))}
                disabled={payrollCurrentPage >= Math.ceil(payrollData.length / payrollItemsPerPage)}
                className="px-3 py-1 text-[14px] text-[#6b6b6b] hover:text-[#0084c7] disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                Nächste
              </button>
            </div>
          </div>

          {/* Search Field at Bottom - only shown when there is data */}
          <div className="flex justify-end">
            <div className="flex items-center gap-2">
              <span className="text-[#6b6b6b] text-[14px]">Suche:</span>
              <input
                type="text"
                placeholder=""
                value={payrollSearchTerm}
                onChange={(e) => {
                  setPayrollSearchTerm(e.target.value);
                  setPayrollCurrentPage(1);
                }}
                className="px-4 py-2 border border-[#e0e0e0] rounded-lg text-[#1c1c1c] focus:outline-none focus:ring-2 focus:ring-[#0084c7] focus:border-transparent w-[200px]"
              />
            </div>
          </div>
        </>
      )}
    </div>
  );
}